import { FileText, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { PageContainer } from '@/components/common/PageContainer'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { BRAND, SCAN_HERO_SOFT } from '@/constants/brand'
import { useAuthStore } from '@/stores/authStore'
import {
  EMPTY_RECENT_DOCUMENT_SCANS,
  useRecentScansStore,
  type RecentDocumentScan,
  type RecentDocumentScanStatus,
} from '@/stores/recentScansStore'
import { getDocumentPlant } from '@/utils/document'
import { getEmployeeCode } from '@/utils/employeeCode'
import { formatRecentScanTime } from '@/utils/recentScans'

const STATUS_LABEL: Record<RecentDocumentScanStatus, string> = {
  scanning: 'Scanning',
  review: 'In review',
  posted: 'Posted',
}

function RecentScanCard({ entry }: { entry: RecentDocumentScan }) {
  const docNo = String(entry.document.DocumentNo ?? '—')
  const customer = String(entry.document.CustName ?? '—')
  const plant = getDocumentPlant(entry.document)

  return (
    <div className="rfid-doc-row cursor-default">
      <span className="rfid-doc-row__icon">
        <FileText className="h-5 w-5" strokeWidth={2} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-extrabold tabular-nums text-foreground">{docNo}</div>
        <div className="mt-0.5 truncate text-[10.5px] font-extrabold" style={{ color: BRAND }}>
          {entry.movementTitle}
        </div>
        <div className="mt-0.5 truncate text-[11.5px] text-muted-foreground">{customer}</div>
        {plant ? (
          <div className="mt-0.5 truncate text-[10.5px] font-bold text-muted-foreground">
            LEAP plant {plant}
          </div>
        ) : null}
        <div className="mt-1 text-[10px] font-semibold text-muted-foreground">
          {entry.tagsScanned} tag{entry.tagsScanned === 1 ? '' : 's'} · {entry.overallProgress}% ·{' '}
          {entry.linesComplete} line{entry.linesComplete === 1 ? '' : 's'} complete
        </div>
      </div>
      <div className="flex max-w-[110px] flex-col items-end gap-1">
        <span
          className="rounded-lg px-2 py-0.5 text-[9px] font-black"
          style={{ color: BRAND, backgroundColor: `${BRAND}18` }}
        >
          {STATUS_LABEL[entry.status] ?? entry.status}
        </span>
        <span className="text-right text-[10px] font-semibold text-muted-foreground">
          {formatRecentScanTime(entry.updatedAt)}
        </span>
        {entry.sapGeneratedDocument ? (
          <span className="max-w-full truncate text-[9px] font-bold text-muted-foreground">
            SAP {entry.sapGeneratedDocument}
          </span>
        ) : null}
      </div>
    </div>
  )
}

export default function RecentScansPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const employeeCode = getEmployeeCode(user)
  const userKey = employeeCode || user?.userId || ''

  const entriesFromStore = useRecentScansStore((s) => {
    if (!userKey) return undefined
    const byCode = employeeCode ? s.byUser[employeeCode] : undefined
    const byUid = user?.userId ? s.byUser[user.userId] : undefined
    return byCode ?? byUid
  })
  const entries = entriesFromStore ?? EMPTY_RECENT_DOCUMENT_SCANS
  const clearForUser = useRecentScansStore((s) => s.clearForUser)

  const onClear = () => {
    if (employeeCode) clearForUser(employeeCode)
    if (user?.userId && user.userId !== employeeCode) clearForUser(user.userId)
    if (!employeeCode && !user?.userId) return
    toast.message('Recent scans cleared')
  }

  return (
    <PageContainer fluid className="!px-0 !py-0" stack={false}>
      <div className="rfid-web-frame">
        <RfidAppHeader
          title="Recent scans"
          subtitle="Documents you opened and scanned on this device"
          onBack={() => navigate('/')}
        />

        <div className="rfid-wizard-body flex flex-1 flex-col gap-3 overflow-y-auto">
          <div
            className="flex flex-col items-center rounded-2xl px-4 py-5"
            style={{ backgroundColor: SCAN_HERO_SOFT }}
          >
            <div className="text-4xl font-black tabular-nums leading-none text-white">
              {entries.length}
            </div>
            <div className="mt-1 text-center text-[11px] font-bold text-white/60">
              Documents recorded
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="text-[13px] font-black text-foreground">History</div>
            {entries.length > 0 ? (
              <button
                type="button"
                onClick={onClear}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground"
              >
                <Trash2 className="h-4 w-4" />
                Clear
              </button>
            ) : null}
          </div>

          {entries.length === 0 ? (
            <p className="m-0 py-6 text-center text-xs font-semibold text-muted-foreground">
              No recent document scans yet
            </p>
          ) : (
            <div>
              {entries.map((entry) => (
                <RecentScanCard key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  )
}
